// Core imports
import { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import i18n from './i18n';
import store, { history } from './store';

// Component imports
import { Spin } from 'antd';
import Root from './screens/Root';

// Misc imports
import { GOOGLE_ANALYTICS_KEY } from './constants';
import { isDevEnv } from './utils/is/isDev';
import './styles/css/main.css';

function App() {
  useEffect(() => {
    if (!GOOGLE_ANALYTICS_KEY) {
      return undefined;
    }

    ReactGA.initialize(GOOGLE_ANALYTICS_KEY);

    const sendPageview = (pathname, search) => ReactGA.send({
      hitType: 'pageview',
      page: `${pathname}${search || ''}`
    });

    sendPageview(window.location.pathname, window.location.search);

    // This is a SPA, so clicking a <Link> never remounts App. Without this
    // listener GA only ever recorded the URL a visitor landed on, and any page
    // people reach by clicking rather than by search looked like it had no
    // traffic at all. /donate is the extreme case: nobody arrives there from
    // Google, so it registered as essentially zero visits.
    const unlisten = history.listen((update) => {
      const location = update?.location || update;
      sendPageview(location.pathname, location.search);
    });

    if (isDevEnv()) {
      console.log('Google Analytics initialized with key:', GOOGLE_ANALYTICS_KEY);
    }

    return unlisten;
  }, []);

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <Router history={history}>
            <Suspense fallback={<Spin />}>
              <Root />
            </Suspense>
          </Router>
        </Provider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
